import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useFetcher } from '@remix-run/react';
import { Check, CheckCircle2, LoaderCircle, ShieldCheck } from 'lucide-react';
import { Fragment, useEffect, useState } from 'react';

type ProfileCompletionModalProps = {
    isOpen: boolean;
    onClose: () => void;
    onSkip: () => void;
    isProfileComplete?: boolean;
};

export function ProfileCompletionModal({
    isOpen,
    onClose,
    onSkip,
    isProfileComplete = false,
}: ProfileCompletionModalProps) {
    const fetcher = useFetcher<{ success?: boolean; message?: string }>();
    const [step, setStep] = useState(isProfileComplete ? 2 : 1);

    useEffect(() => {
        if (fetcher.data?.success) {
            setStep(2);
        }
    }, [fetcher.data]);

    const handleSkip = () => {
        onSkip();
    };

    const handleClose = () => {
        if (step === 2) {
            onClose();
        } else {
            handleSkip();
        }
    };

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={handleClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/50" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-90"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-90"
                        >
                            <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                                <div className="absolute right-4 top-4 z-10">
                                    <button
                                        type="button"
                                        onClick={handleClose}
                                        className="rounded-full p-1 hover:bg-gray-100 transition-colors"
                                    >
                                        <XMarkIcon className="h-6 w-6 text-gray-400 hover:text-gray-600" />
                                    </button>
                                </div>

                                <div className="flex flex-col items-center text-center w-full mt-8">
                                    {/* Simple Stepper */}
                                    <div className="flex items-center justify-center w-full mb-8 px-4">
                                        <div className="flex flex-col items-center gap-2">
                                            <div className={`flex items-center justify-center w-10 h-10 rounded-full transition-colors ${step >= 1 ? 'bg-primary text-white' : 'bg-gray-100 text-gray-400'}`}>
                                                {step > 1 ? <Check className="w-5 h-5" /> : <span className="font-semibold">1</span>}
                                            </div>
                                            <span className={`text-xs font-medium ${step >= 1 ? 'text-primary' : 'text-gray-500'}`}>Profile</span>
                                        </div>

                                        <div className={`flex-1 h-0.5 mx-4 mb-6 transition-colors ${step >= 2 ? 'bg-primary' : 'bg-gray-200'}`} />

                                        <div className="flex flex-col items-center gap-2">
                                            <div className={`flex items-center justify-center w-10 h-10 rounded-full transition-colors ${step >= 2 ? 'bg-primary text-white' : 'bg-gray-100 text-gray-400'}`}>
                                                <span className="font-semibold">2</span>
                                            </div>
                                            <span className={`text-xs font-medium ${step >= 2 ? 'text-primary' : 'text-gray-500'}`}>KYC</span>
                                        </div>
                                    </div>

                                    <Dialog.Title
                                        as="h3"
                                        className="text-2xl font-bold text-gray-900 mb-2"
                                    >
                                        {step === 1 ? 'Complete Your Profile' : 'Verify Your Identity'}
                                    </Dialog.Title>

                                    <p className="text-sm text-gray-600 mb-6">
                                        {step === 1
                                            ? 'Help us personalize your experience'
                                            : 'Complete KYC verification to unlock all features'}
                                    </p>

                                    {step === 1 ? (
                                        <fetcher.Form
                                            method="post"
                                            action="/api/update-profile"
                                            className="w-full space-y-4"
                                        >
                                            <div className="grid grid-cols-2 gap-3">
                                                <div className="text-left">
                                                    <label
                                                        htmlFor="firstName"
                                                        className="block text-sm font-medium text-gray-700 mb-1"
                                                    >
                                                        First Name
                                                    </label>
                                                    <input
                                                        id="firstName"
                                                        name="firstName"
                                                        type="text"
                                                        autoComplete="given-name"
                                                        placeholder="John"
                                                        disabled={fetcher.state !== 'idle'}
                                                        className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary sm:text-sm disabled:bg-gray-50 disabled:cursor-not-allowed transition-colors"
                                                    />
                                                </div>

                                                <div className="text-left">
                                                    <label
                                                        htmlFor="lastName"
                                                        className="block text-sm font-medium text-gray-700 mb-1"
                                                    >
                                                        Last Name
                                                    </label>
                                                    <input
                                                        id="lastName"
                                                        name="lastName"
                                                        type="text"
                                                        autoComplete="family-name"
                                                        placeholder="Doe"
                                                        disabled={fetcher.state !== 'idle'}
                                                        className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary sm:text-sm disabled:bg-gray-50 disabled:cursor-not-allowed transition-colors"
                                                    />
                                                </div>
                                            </div>

                                            <div className="text-left">
                                                <label
                                                    htmlFor="email"
                                                    className="block text-sm font-medium text-gray-700 mb-1"
                                                >
                                                    Email Address
                                                </label>
                                                <input
                                                    id="email"
                                                    name="email"
                                                    type="email"
                                                    autoComplete="email"
                                                    placeholder="john@example.com"
                                                    disabled={fetcher.state !== 'idle'}
                                                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary sm:text-sm disabled:bg-gray-50 disabled:cursor-not-allowed transition-colors"
                                                />
                                            </div>

                                            {fetcher.data?.message && !fetcher.data.success && (
                                                <div className="rounded-lg bg-red-50 p-3 text-left">
                                                    <p className="text-sm text-red-800">
                                                        {fetcher.data.message}
                                                    </p>
                                                </div>
                                            )}

                                            <div className="flex flex-col gap-3 w-full pt-2">
                                                <button
                                                    type="submit"
                                                    disabled={fetcher.state !== 'idle'}
                                                    className="w-full inline-flex justify-center items-center gap-2 px-6 py-3 border border-transparent text-base font-semibold rounded-lg text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-4 focus:ring-primary/20 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    {fetcher.state !== 'idle' && (
                                                        <LoaderCircle className="animate-spin h-5 w-5" />
                                                    )}
                                                    {fetcher.state !== 'idle' ? 'Saving...' : 'Save & Continue'}
                                                </button>
                                            </div>
                                        </fetcher.Form>
                                    ) : (
                                        <div className="w-full space-y-4">
                                            <div className="bg-green-50 p-4 rounded-lg border border-green-100 mb-4">
                                                <div className="flex items-center gap-2">
                                                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                                                    <p className="text-green-800 text-sm font-medium">
                                                        Profile details saved successfully!
                                                    </p>
                                                </div>
                                            </div>

                                            <a
                                                href="/account/kyc"
                                                className="w-full inline-flex justify-center items-center gap-2 px-6 py-3 border border-transparent text-base font-semibold rounded-lg text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-4 focus:ring-primary/20 transition-all shadow-sm"
                                            >
                                                <ShieldCheck className="h-5 w-5" />
                                                Fill KYC Now
                                            </a>

                                            <div className="w-full">
                                                <button
                                                    type="button"
                                                    onClick={onClose}
                                                    className="w-full inline-flex justify-center items-center px-4 py-3 border border-gray-300 text-base font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-4 focus:ring-gray-200 transition-all"
                                                >
                                                    Skip
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition >
    );
}
